/**
 * Actual vs the one selected plan target. No extra horizons on the graph.
 */

import React from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KpiTileShell } from './KpiTileShell';
import { ANNUAL_RING, fmtKpiValue, fmtKpiWithUnit, gaugeFill, type KpiTile } from './types';

interface KpiCompareCardProps {
  tile: KpiTile;
  t: (key: string) => string;
  onHide?: () => void;
}

export const KpiCompareCard: React.FC<KpiCompareCardProps> = ({ tile, t, onHide }) => {
  const rows = [
    {
      name: t('kpi.actual') || 'Actual',
      value: tile.actual,
      fill: tile.color ? gaugeFill(tile.color) : 'var(--color-primary-text)'
    },
    {
      name: t('kpi.target') || 'Plan',
      value: tile.primaryTarget,
      fill: ANNUAL_RING
    }
  ].filter((row) => row.value !== null && row.value !== undefined) as Array<{
    name: string;
    value: number;
    fill: string;
  }>;

  return (
    <KpiTileShell
      name={tile.kpiName}
      hideLabel={t('kpi.tileHide') || 'Hide from this tab'}
      onHide={onHide}
    >
      <p
        className="text-sm font-bold leading-none mb-1 text-center shrink-0"
        style={{ color: tile.color ? gaugeFill(tile.color) : 'var(--color-surface-text)' }}
      >
        {tile.redacted ? '—' : fmtKpiWithUnit(tile, tile.actual)}
      </p>
      <div className="h-20 min-h-0 shrink-0">
        {tile.redacted || rows.length === 0 ? (
          <p className="text-[10px] text-[var(--color-secondary-text)]">—</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={rows} margin={{ top: 0, right: 52, left: 0, bottom: 0 }}>
              <XAxis type="number" hide domain={[0, 'auto']} />
              <YAxis
                type="category"
                dataKey="name"
                width={48}
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(value: number) => [fmtKpiValue(tile, value), '']}
              />
              <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={10} isAnimationActive={false}>
                {rows.map((row) => (
                  <Cell key={row.name} fill={row.fill} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(value: number) => fmtKpiValue(tile, value)}
                  style={{ fontSize: 9, fill: 'var(--color-surface-text)' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </KpiTileShell>
  );
};
